import type {
  MetaAccess,
  MetaAccountAssignmentStatus,
  MetaAccountUserConnectResult,
  MetaAccountUserHealth,
  MetaAccountUsersList,
} from "~/ui-rtk/api/types/meta-account.types"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import type { CompanySuggestion } from "./meta-access.utils"

/**
 * Every user-facing string of Settings → Company → Meta access (APP-566 T2).
 * Skip reasons (`detail`) and error messages come from the API and are shown
 * verbatim; nothing here rephrases them.
 */

/** `?metaAccess=manage` on Settings → Company opens Manage account users. */
export const META_ACCESS_PARAM = "metaAccess"
export const META_ACCESS_MANAGE = "manage"
/** Where alerts, the agency dialog and the way back from Facebook's dialog link to. */
export const META_ACCESS_MANAGE_URL = `${brandRoutes.SETTINGS.COMPANY}?${META_ACCESS_PARAM}=${META_ACCESS_MANAGE}`

/** 'YYYY-MM-DD' from an ISO timestamp (UTC), as the agency credential dialog shows dates. */
export function isoDay(iso: string): string {
  return iso.slice(0, 10)
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

/* ----- Card ----- */

export const META_ACCESS_TITLE = "Meta access"

export const META_ACCESS_INTRO =
  "Account users are Marathon people's Facebook logins, shared out to companies one assignment at a time. Today an assignment is used only for partnership content discovery."

/** What an assignment is used for, per `metaCredentials.tier2` value, in the intro's order (APP-566 T5). */
const ASSIGNMENT_USES: ReadonlyArray<[string, string]> = [
  ["discovery", "partnership content discovery"],
  ["read", "Meta reads"],
  ["edit", "Meta edits"],
  ["creator_permissions", "the creator-permissions read"],
  ["partnership_launch", "partnership launches"],
]

/** The values T5 adds (APP-566 T5): without one, the intro is today's. */
const T5_VALUES: ReadonlySet<string> = new Set([
  "read",
  "edit",
  "creator_permissions",
  "partnership_launch",
])

/** "a", "a and b", "a, b and c". */
function listOf(items: string[]): string {
  return items.length > 1
    ? `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`
    : (items[0] ?? "")
}

/** The card's intro for `flags.tier2` (APP-566 T5, D25): `META_ACCESS_INTRO` while no T5 value is listed. */
export function metaAccessIntro(tier2: readonly string[]): string {
  if (!tier2.some(value => T5_VALUES.has(value))) return META_ACCESS_INTRO
  const uses = ASSIGNMENT_USES.filter(([value]) => tier2.includes(value)).map(
    ([, use]) => use,
  )
  return `Account users are Marathon people's Facebook logins, shared out to companies one assignment at a time. An assignment is used for ${listOf(uses)}, after the company's Meta Advanced system user where one serves.`
}

export const META_ACCESS_LOAD_ERROR =
  "Couldn't load Meta access. Reload the page to try again."

export const DISCOVERY_LINE_LABEL = "Partnership discovery"

/** What partnership discovery runs on for this company (the API's selection). */
export function discoveryServedText(
  servedBy: MetaAccess["capabilities"][number]["servedBy"],
): string {
  if (!servedBy) return "does not run: no usable login"
  switch (servedBy.candidate) {
    case "account_user":
      return servedBy.name
        ? `runs on account user ${servedBy.name}`
        : "runs on an account user"
    case "connector_person":
      return "runs on the Instagram Insights connector's login"
    case "agency":
      return servedBy.name
        ? `runs on the discovery default login ${servedBy.name}`
        : "runs on the discovery default login"
    // Only a T5 row names a system user (APP-566 T5); discovery never runs on one.
    case "system_user":
      return "runs on the Meta Advanced system user"
  }
}

/** The label of a T5 capability line (APP-566 T5, D25). */
export const CAPABILITY_LINE_LABEL: Record<
  Exclude<MetaAccess["capabilities"][number]["capability"], "discovery">,
  string
> = {
  read: "Meta reads",
  edit: "Meta edits",
  creator_permissions: "Creator-permissions read",
  partnership_launch: "Partnership launches",
}

/** What a T5 capability runs on for this company (the API's resolver decision). */
export function capabilityServedText(
  servedBy: MetaAccess["capabilities"][number]["servedBy"],
): string {
  if (!servedBy) return "no usable credential"
  switch (servedBy.candidate) {
    case "system_user":
      return "run on the Meta Advanced system user"
    case "account_user":
      return servedBy.name
        ? `run on account user ${servedBy.name}`
        : "run on an account user"
    case "connector_person":
      return "run on the Instagram Insights connector's login"
    // Only discovery runs on the discovery default login.
    case "agency":
      return "run on the discovery default login"
  }
}

export const META_ACCESS_EMPTY = "No account user is assigned to this company."

export const ROW_LABEL = {
  accountUser: "Account user",
  adAccount: "Ad account",
  instagram: "Instagram",
  page: "Page",
  business: "Business",
  assigned: "Assigned",
  lastChecked: "Last checked",
} as const

/** An empty asset on an assignment. */
export const NONE = "None"

export const BUTTON = {
  assign: "Assign",
  change: "Change",
  edit: "Edit",
  pause: "Pause",
  resume: "Resume",
  unassign: "Unassign",
  manage: "Manage account users",
  keepIt: "Keep it",
  cancel: "Cancel",
  save: "Save",
  close: "Close",
  tryAgain: "Try again",
} as const

/** "act_123 · Brand Ads" / "act_123". */
export function adAccountLine(id: string, name: string | null): string {
  return name ? `act_${id} · ${name}` : `act_${id}`
}

/** "@brand · 1784…" / "1784…". */
export function instagramLine(id: string, username: string | null): string {
  return username ? `@${username} · ${id}` : id
}

/** "Name · id" / "id" (Page, business). */
export function namedLine(id: string, name: string | null): string {
  return name ? `${name} · ${id}` : id
}

/** "on 2026-10-15 by Tom" / "on 2026-10-15". */
export function onDayBy(iso: string, name: string | null): string {
  return name ? `on ${isoDay(iso)} by ${name}` : `on ${isoDay(iso)}`
}

export function lastCheckedText(iso: string | null): string {
  return iso ? `on ${isoDay(iso)}` : "not yet"
}

export const ASSIGNMENT_STATUS_LABEL: Record<
  MetaAccountAssignmentStatus,
  string
> = {
  active: "Active",
  paused: "Paused",
  lost_reach: "Lost reach",
  login_paused: "Login paused",
  login_invalid: "Login not working",
  login_expired: "Login expired",
}

export function unassignConfirmText(company: string): string {
  return `Unassign ${company}? Partnership discovery moves to the next login in line.`
}

export const TOAST = {
  assigned: (company: string) => `Meta access assigned to ${company}.`,
  updated: (company: string) => `Meta access updated for ${company}.`,
  paused: (company: string) => `Meta access paused for ${company}.`,
  resumed: (company: string) => `Meta access resumed for ${company}.`,
  removed: (company: string) => `Meta access removed from ${company}.`,
} as const

/** Fallback when an API error carries no message (a network failure). */
export const CHANGE_FAILED = "Couldn't update Meta access. Try again."

/* ----- Ad-account binding (APP-589) ----- */

/**
 * The company's ad-account binding (the Instagram Insights connector's
 * credentials `metadata.adAccountId`), set here since the separate Meta Ad
 * Account card went. The API texts that ask for a binding name this line.
 */
export const BINDING_LABEL = "Bound ad account"

export const BINDING_PLACEHOLDER = "Select an ad account…"

export const BINDING_NO_ACCOUNTS =
  "No Meta ad accounts accessible to this company's token."

export const BINDING_SAVED = "Meta ad account updated."

/** Fallback when an API error carries no message (a network failure). */
export const BINDING_SAVE_FAILED = "Couldn't update the ad account. Try again."

/* ----- Manage account users ----- */

export const MANAGE_TITLE = "Meta account users"

export const MANAGE_HELP =
  "An account user is one Marathon person's Facebook login. Connect it once, then assign each company the ad account, Instagram account, Page and business it may use. Logins are stored encrypted and never shown."

export const CONNECT_HELP =
  "In Facebook's dialog, tick every business, Instagram account and Page this person should serve. Meta keeps one authorisation per person: what you tick replaces what was ticked before, for every company."

export const CONNECT_WARNING =
  "Do not use this Facebook account to connect a brand's Instagram Insights connector."

export const CONNECT_LABEL = "Connect with Facebook"

export const CONNECT_START_FAILED =
  "Couldn't start the Facebook login. Try again."

export const MANAGE_LOADING = "Loading…"
export const MANAGE_LOAD_ERROR = "Couldn't load the account users."
export const MANAGE_EMPTY =
  "No account users yet. Connect a Facebook login to start."

/** The daily health check line: muted when scheduled, amber when not. */
export function healthCheckLine(
  healthCheck: MetaAccountUsersList["healthCheck"],
): { text: string; warning: boolean } {
  if (!healthCheck.scheduled) {
    return {
      text: "The daily health check is not scheduled. It is created when you connect a login while an active company is open, or in Admin → Scheduled Jobs (meta-account-user-health, 0 5 * * *, UTC).",
      warning: true,
    }
  }
  return {
    text: healthCheck.lastRunAt
      ? `Daily health check at 05:00 UTC. Last run ${isoDay(healthCheck.lastRunAt)}.`
      : "Daily health check at 05:00 UTC. Last run: not yet.",
    warning: false,
  }
}

export function singlePointOfFailureText(names: string[]): string {
  const n = names.length
  return `${n} ${plural(n, "company relies", "companies rely")} on one account user only (no system user, no working client login): ${names.join(", ")}.`
}

export const LOGIN_CHIP = {
  discoveryDefault: "Discovery default",
  paused: "Paused",
  notWorking: "Not working",
  expiresSoon: "Expires soon",
  expired: "Expired",
} as const

export const LOGIN_ROW_LABEL = {
  facebookAccount: "Facebook account",
  permissions: "Permissions",
  expires: "Expires",
  dataAccess: "Data access",
  assignments: "Assignments",
  coverage: "Coverage",
  connected: "Connected",
  lastProblem: "Last problem",
} as const

export const UNKNOWN_PERSON = "unknown — reconnect to record who this is"

export const NO_EXPIRY = "no expiry reported"

export function grantedText(n: number): string {
  return `${n} granted`
}

export function missingScopesText(scopes: string[]): string {
  return `Missing: ${scopes.join(", ")}`
}

export function optionalScopesText(scopes: string[]): string {
  return `Not granted (optional): ${scopes.join(", ")}`
}

export function coverageText(covered: number, total: number): string {
  return `covers ${covered} of ${total} assignments`
}

export const COVERAGE_TITLE =
  "From the stored Facebook authorisation; ad accounts are checked when you move or assign"

export const LOGIN_ACTION = {
  reconnect: "Reconnect",
  pause: "Pause",
  resume: "Resume",
  makeDefault: "Make discovery default",
  distribute: "Distribute",
  move: "Move assignments",
  remove: "Remove",
  removeConfirm: "Remove login",
} as const

/** A login's name in a sentence. */
export function loginNameOf(name: string | null | undefined): string {
  return name ?? "this account user"
}

/** A login's name in a list, a heading or an option. */
export function loginLabelOf(
  connectedUser: { id: string; name: string | null } | null,
): string {
  return connectedUser?.name ?? connectedUser?.id ?? "Unknown Facebook account"
}

export function removeConfirmText(
  name: string | null,
  isDiscoveryDefault: boolean,
): string {
  const base = `Remove ${loginNameOf(name)}? Marathon forgets this login. Meta keeps it valid until the person removes the MarathonData app in their Facebook settings.`
  return isDiscoveryDefault
    ? `${base} Partnership discovery stops for brands with a system user and no assignment until another login is the discovery default.`
    : base
}

const UNHEALTHY_WORD: Partial<Record<MetaAccountUserHealth, string>> = {
  paused: "paused",
  invalid: "not working",
  expired: "expired",
}

export function defaultConfirmText(
  name: string | null,
  health: MetaAccountUserHealth,
): string {
  const base = `Make ${loginNameOf(name)} the discovery default? Brands with a system user and no assignment discover through it.`
  const word = UNHEALTHY_WORD[health]
  return word
    ? `${base} This login is ${word}, so discovery skips it until that is fixed.`
    : base
}

/** " (paused)" / " (not working)" / " (expired)" after a login that cannot be picked. */
export function unusableSuffixOf(health: MetaAccountUserHealth): string {
  const word = UNHEALTHY_WORD[health]
  return word ? ` (${word})` : ""
}

/** The toast after Connect / Reconnect comes back from Facebook. */
export function connectResultText(
  result: MetaAccountUserConnectResult,
): string {
  const name = result.user.connectedUser?.name ?? "the account user"
  const { checked, lostReach } = result.recheck
  const checkedPart = `${checked} ${plural(checked, "assignment", "assignments")} checked`
  const lostPart =
    lostReach.length > 0
      ? `, ${lostReach.length} lost reach: ${lostReach.map(l => l.companyName).join(", ")}`
      : ""
  return `Connected ${name}. ${checkedPart}${lostPart}.`
}

export const CONNECT_FAILED =
  "Couldn't connect the account user. Start again from Meta access."

export const CONNECT_CANCELLED =
  "The Facebook login did not finish. Nothing changed."

/* ----- Move assignments ----- */

export function moveTitle(name: string | null): string {
  return `Move assignments from ${loginNameOf(name)}`
}

export const MOVE_TO_LABEL = "Move to"

export function moveButtonText(n: number): string {
  return `Move ${n} ${plural(n, "assignment", "assignments")}`
}

export function movedText(moved: number, total: number): string {
  return `Moved ${moved} of ${total}.`
}

export const NOT_MOVED_LABEL = "Not moved:"

export function notMovedLine(company: string, reason: string): string {
  return `${company} — ${reason}`
}

export const MOVE_FAILED = "Couldn't move the assignments. Try again."

/* ----- Assign ----- */

export function assignTitle(company: string): string {
  return `Assign Meta access — ${company}`
}

export const ACCOUNT_USER_LABEL = "Account user"

export const ASSIGN_HELP =
  "Only assets this login reaches are listed. An asset another company uses is shown but cannot be picked."

export const PICKER_LABEL = {
  adAccount: "Ad account",
  instagram: "Instagram account",
  page: "Page",
  business: "Business",
} as const

/** "{name} ({id})", or the id alone when Meta gave no name. */
export function optionLabel(id: string, name: string | null): string {
  return name ? `${name} (${id})` : id
}

/** A held asset: shown, never pickable. */
export function heldOptionLabel(label: string, company: string): string {
  return `${label} — used by ${company}`
}

/** The stored or pre-filled id when this login's list does not include it. */
export function unlistedOptionLabel(label: string): string {
  return `${label} — not in this login's list`
}

export const PREFILL_HINT =
  "Pre-filled from this company's Instagram Insights connector."

export const INVENTORY_LOADING = "Reading what this login reaches…"

export const INVENTORY_LOAD_FAILED =
  "Couldn't read what this login reaches. Try again."

export const NO_USABLE_LOGINS =
  "No account user can be assigned: connect or resume one in Manage account users."

export function readFromMetaText(relative: string): string {
  return `Read from Meta ${relative}.`
}

export const REFRESH_FROM_META = "Refresh from Meta"

export const ASSIGN_FAILED = "Couldn't save the assignment. Try again."

/* ----- Distribute ----- */

export function distributeTitle(name: string | null): string {
  return `Distribute ${name ?? "this account user"}'s access`
}

export const DISTRIBUTE_TAB_COMPANIES = "Companies"

export function unassignedAssetsTab(n: number): string {
  return `Unassigned assets (${n} new)`
}

export const DISTRIBUTE_COLUMN = {
  company: "Company",
  assignment: "Assignment",
  suggestion: "Suggestion",
} as const

export const ONLY_UNASSIGNED_TOGGLE = "Only companies without an assignment"

export const NO_UNASSIGNED_COMPANIES =
  "Every company already has an assignment."

export const NO_UNASSIGNED_ASSETS =
  "Nothing new: every asset this login reaches is used by a company."

export const COMPANIES_LOAD_ERROR = "Couldn't load the companies."

export const ASSET_GROUP = {
  adAccounts: "Ad accounts",
  instagramAccounts: "Instagram accounts",
  pages: "Pages",
} as const

export const ASSIGN_TO = "Assign to…"

/** The Suggestion column: an exact id match, a name match, or "—". */
export function suggestionTextOf(
  suggestion: CompanySuggestion | null | undefined,
): string {
  if (!suggestion) return "—"
  switch (suggestion.kind) {
    case "instagram":
      return `Instagram ${suggestion.username ? `@${suggestion.username}` : suggestion.igUserId} matches the connector`
    case "ad_account_binding":
      return `Ad account act_${suggestion.adAccountId} matches the binding`
    case "ad_account_fivetran":
      return `Ad account act_${suggestion.adAccountId} matches the connector's synced ad accounts`
    case "name":
      return `Name match: @${suggestion.username ?? suggestion.igUserId} (${Math.round(suggestion.score * 100)}%)`
  }
}
